const IDENTITY_BASE_URL = process.env.REACT_APP_IDENTITY_BASE_URL;

type AuthQueryParamValues = {
  email?: string;
  redirectUrl?: string;
  prompt?: string;
  experience?: string;
};

const buildAuthQueryParams = (
  urlType: 'identity' | 'forums',
  { email, redirectUrl, prompt, experience = 'entry-auth' }: AuthQueryParamValues,
) => {
  const queryParams = [];

  if (email) {
    queryParams.push(`login_hint=${email}`);
  }
  if (experience) {
    // For 'community' experience, don't add the suffix. This is for account switching.
    const applicationValue = experience === 'community' ? 'community' : `community--${experience}`;
    queryParams.push(`application=${applicationValue}`);
  }
  if (redirectUrl) {
    queryParams.push(urlType === 'identity' ? `continue=${redirectUrl}` : `referer=${redirectUrl}`);
  }
  if (prompt) {
    queryParams.push(`prompt=${prompt}`);
  }
  return queryParams;
};

export const getAuthUrl = (type: 'login' | 'signup' | 'logout', queryParamValues: AuthQueryParamValues): string => {
  const queryParams = buildAuthQueryParams('identity', queryParamValues);
  return `${IDENTITY_BASE_URL}/${type}?${queryParams.join('&')}`;
};

export const getForumsSignInUrl = (queryParamValues: AuthQueryParamValues): string => {
  // Only include referer parameter for OAuth2 SSO redirect
  const queryParams = [];
  if (queryParamValues.redirectUrl) {
    queryParams.push(`referer=${queryParamValues.redirectUrl}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  return `/forums/s/plugins/common/feature/oauth2sso_v2/sso_login_redirect${queryString}`;
};

export const getForumsSignOutUrl = (khorosEndpointPath: string): string =>
  `forums/s/plugins/custom/atlassian/${khorosEndpointPath}/khoros_logout`;

/**
 * TEMPORARY (pre-CloudFront `/forums` migration):
 *
 * Higher Logic Vanilla (HLV) currently lives on a separate origin, so its post-consent
 * return URL is an absolute, external URL such as:
 *   https://atlassian.vanillastaging.com/entry/connect/oauth2?stashID=...
 *
 * The default redirect handling assumes a relative, same-origin path and would mangle
 * or drop this. This helper recognises a genuine HLV return URL (by host + entry path)
 * so it can be honoured verbatim, while rejecting anything else to avoid open redirects.
 *
 * Once HLV is served under `community.atlassian.com/forums` via CloudFront, the redirect
 * becomes same-origin (like Khoros) and this helper can be removed.
 *
 * Matches the three HLV environments, restricted to the `/entry/` callback path:
 *   - dev:  https://atlassian.vanilla.development.com/entry/...
 *   - stg:  https://atlassian.vanillastaging.com/entry/...
 *   - prod: https://atlassian.vanilla.com/entry/...
 *
 * @param redirectParam the raw `redirect` query param value
 * @returns the URL if it is a valid HLV return URL, otherwise null
 */
const HLV_RETURN_URL_PATTERN =
  /^https:\/\/atlassian\.(vanilla\.development|vanillastaging|vanilla)\.com\/entry\//i;

export const getAllowedExternalRedirect = (
  redirectParam: string | null | undefined,
): string | null => {
  if (!redirectParam) {
    return null;
  }
  return HLV_RETURN_URL_PATTERN.test(redirectParam) ? redirectParam : null;
};
